/*
 * Project: Goalscape
 * File:    memberUtils.js
 * Path:    src/utils/memberUtils.js
 * Description: Utility functions for managing project members and their roles.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement real-time updates for member roles using WebSockets for a more interactive experience.
 */
import { doc, updateDoc, getDocs, query, collection, where, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/*
 * Function: addProjectMember
 * Description: Adds a new member to a project with collaborator role.
 * Parameters:
 *   projectId: string - The ID of the project.
 *   email: string - The email of the user to add.
 *   currentUser: object - The currently logged-in user.
 * Returns: Promise<void>
 * Usage:
 *   await addProjectMember('project123', 'user@example.com', currentUser);
 * AI Hint: Implement invitation system with pending status for users who haven't yet joined the platform.
 */
export const addProjectMember = async (projectId, email, currentUser) => {
    const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where('email', '==', email.trim()))
    );

    if (usersSnapshot.empty) {
        throw new Error('User not found');
    }

    const userData = usersSnapshot.docs[0];
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.data().members[userData.id]) {
        throw new Error('User is already a member of this project');
    }

    await updateDoc(projectRef, {
        [`members.${userData.id}`]: {
            role: 'collaborator',
            addedAt: new Date().toISOString(),
            addedBy: currentUser.uid
        }
    });
};

/*
 * Function: updateMemberRole
 * Description: Updates a member's role in a project.
 * Parameters:
 *   projectId: string - The ID of the project.
 *   memberId: string - The ID of the member.
 *   newRole: string - The new role to assign ('admin' or 'collaborator').
 * Returns: Promise<void>
 * Usage:
 *   await updateMemberRole('project123', 'user456', 'admin');
 * AI Hint: Implement role-based access control (RBAC) to restrict access to certain features based on member roles.
 */
export const updateMemberRole = async (projectId, memberId, newRole) => {
    await updateDoc(doc(db, 'projects', projectId), {
        [`members.${memberId}.role`]: newRole
    });
};

/*
 * Function: removeMember
 * Description: Removes a member from a project, preventing removal of the last admin.
 * Parameters:
 *   projectId: string - The ID of the project.
 *   memberId: string - The ID of the member to remove.
 * Returns: Promise<void>
 * Usage:
 *   await removeMember('project123', 'user456');
 * AI Hint: Implement audit logging to track member additions and removals for security purposes.
 */
export const removeMember = async (projectId, memberId) => {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    const projectData = projectDoc.data();

    const adminCount = Object.entries(projectData.members).filter(
        ([_, member]) => member.role === 'admin'
    ).length;

    if (projectData.members[memberId].role === 'admin' && adminCount <= 1) {
        throw new Error('Cannot remove the last admin');
    }

    const updatedMembers = { ...projectData.members };
    delete updatedMembers[memberId];

    await updateDoc(projectRef, { members: updatedMembers });
};